import type { RootStore } from './RootStore';

/**
 * Un aviso que el server guardó (UserNotification), como lo manda por SSE: uno
 * nuevo en el evento 'notification' (SSEManager.createAndSendNotification y
 * createAlertNotification desde el worker), y los sin leer ni archivar del
 * usuario en 'notifications.unread', que el server manda en cada conexión
 * (SSEManager.loadUnreadNotifications).
 */
interface ServerNotificationPayload {
  id: string;
  priority?: string | null;
  title: string;
  body?: string | null;
  actionUrl?: string | null;
  objectType?: string | null;
  objectId?: string | null;
  createdAt?: string | null;
  // La clave del texto del aviso, sus parámetros y su variante: el cliente lo
  // escribe en el idioma del usuario (common/notifications/notificationText.ts).
  metadata?: { i18nKey?: string; params?: Record<string, unknown>; variant?: string } | null;
}

/**
 * El aviso del server como aviso de la campana. La fecha es la del server
 * (createdAt): un aviso reenviado en una reconexión no dice «ahora».
 */
const toNotification = (n: ServerNotificationPayload) => ({
  type: n.priority === 'high' ? ('warning' as const) : ('info' as const),
  title: n.title,
  message: n.body || '',
  timestamp: n.createdAt,
  data: {
    userNotificationId: n.id,
    actionUrl: n.actionUrl,
    objectType: n.objectType,
    objectId: n.objectId,
    ...(n.metadata?.i18nKey
      ? {
          i18nKey: n.metadata.i18nKey,
          i18nParams: n.metadata.params ?? {},
          ...(n.metadata.variant ? { i18nVariant: n.metadata.variant } : {}),
        }
      : {}),
  },
});

/**
 * Registra los handlers SSE de los avisos que guarda el server. Lo llama el
 * constructor del RootStore. Que un aviso que ya está en la campana no se repita
 * lo resuelve NotificationStore.addNotification, por su userNotificationId.
 */
export function registerNotificationSSEHandlers(rootStore: RootStore): void {
  const { eventSourceStore, notificationStore } = rootStore;

  eventSourceStore.registerHandler('notification', (payload: ServerNotificationPayload) => {
    notificationStore.addNotification(toNotification(payload));
  });

  eventSourceStore.registerHandler(
    'notifications.unread',
    (payload: { notifications?: ServerNotificationPayload[] }) => {
      if (payload.notifications && Array.isArray(payload.notifications)) {
        for (const n of payload.notifications) {
          notificationStore.addNotification(toNotification(n));
        }
      }
    }
  );
}
